import React, { useState, useRef, useEffect } from "react";
import "./Geofence.css";
import { Button, Input } from ".";
import { buttonClasses } from "./theme";

const Geofence = ({ onCoordinatesChange, factoryCoordinates: initialCoordinates = [], readOnly = false, noExtraField=false }) => {
	const [map, setMap] = useState(null);
	const [factoryCoordinates, setFactoryCoordinates] = useState([]);
	const [location, setLocation] = useState("");
	const [latLngInput, setLatLngInput] = useState({ lat: "", lng: "" });
	const [isDrawing, setIsDrawing] = useState(false);
	const mapRef = useRef(null);
	const autocompleteRef = useRef(null);
	const currentPolygonRef = useRef(null); // Ref to store the current polygon
	const markerRef = useRef(null);  // Ref for the marker
	const mapClickListenerRef = useRef(null);
	const polygonListenersRef = useRef([]);
    const [errors, setErrors]= useState(false)

	const getPolygonCoordinates = (polygon) =>
		polygon
			.getPath()
			.getArray()
			.map((latLng) => ({
				lat: latLng.lat(),
				lng: latLng.lng(),
			}));

	const syncCoordinates = (polygon) => {
		const coordinates = getPolygonCoordinates(polygon);
		setFactoryCoordinates(coordinates);
		onCoordinatesChange?.(coordinates);
	};

	const clearPolygonListeners = () => {
		polygonListenersRef.current.forEach((listener) => listener.remove());
		polygonListenersRef.current = [];
	};

	const attachPolygonListeners = (polygon) => {
		if (readOnly) return;
		clearPolygonListeners();
		const updateCoordinates = () => syncCoordinates(polygon);
		polygonListenersRef.current = [
			google.maps.event.addListener(polygon.getPath(), "set_at", updateCoordinates),
			google.maps.event.addListener(polygon.getPath(), "insert_at", updateCoordinates),
			google.maps.event.addListener(polygon.getPath(), "remove_at", updateCoordinates),
			google.maps.event.addListener(polygon, "dragend", updateCoordinates),
		];
	};

	const removeMapClickListener = () => {
		if (mapClickListenerRef.current) {
			mapClickListenerRef.current.remove();
			mapClickListenerRef.current = null;
		}
	};

	const createPolygon = (coordinates, mapInstance) => {
		if (currentPolygonRef.current) {
			currentPolygonRef.current.setMap(null);
		}
		clearPolygonListeners();

		const polygon = new google.maps.Polygon({
			paths: coordinates.map((coord) => ({ lat: coord.lat, lng: coord.lng })),
			editable: !readOnly,
			draggable: !readOnly,
			strokeColor: "#ff7200",
			strokeOpacity: 0.8,
			strokeWeight: 2,
			fillColor: "#ff7200",
			fillOpacity: 0.25,
			map: mapInstance,
		});

		currentPolygonRef.current = polygon;
		attachPolygonListeners(polygon);
		syncCoordinates(polygon);
		return polygon;
	};

	const stopDrawing = () => {
		removeMapClickListener();
		setIsDrawing(false);
	};

	const startDrawing = () => {
		if (readOnly || !map) return;
		clearGeofence();
		setIsDrawing(true);

		const draftCoordinates = [];
		mapClickListenerRef.current = google.maps.event.addListener(map, "click", (event) => {
			const point = {
				lat: event.latLng.lat(),
				lng: event.latLng.lng(),
			};
			draftCoordinates.push(point);

			if (currentPolygonRef.current) {
				currentPolygonRef.current.setPath(draftCoordinates);
				syncCoordinates(currentPolygonRef.current);
				return;
			}

			createPolygon(draftCoordinates, map);
		});
	};

	const initMap = () => {
		if (!window.google || !window.google.maps || !window.google.maps.places) return;

		const google = window.google;
		const mapInstance = new google.maps.Map(mapRef.current, {
			center: { lat: 28.524782, lng: 77.277073 },  // Default center
			zoom: 12,
			scrollwheel: false,
		});

		// Set up the autocomplete service on the input box
		if(!readOnly){
		autocompleteRef.current = new google.maps.places.Autocomplete(document.getElementById("location-input"), {
			types: ["geocode", "establishment"],   // You can adjust this if you want more than just geocodes
		});

		autocompleteRef.current.setFields(["place_id", "geometry", "name"]);

		autocompleteRef.current.addListener("place_changed", () => {
			const place = autocompleteRef.current.getPlace();
			handlePlaceSelection(place, mapInstance);
		});
		}

			setMap(mapInstance);

			// If initial coordinates are provided, create the polygon
			if (initialCoordinates.length > 0) {
						initializePolygon(initialCoordinates, mapInstance);
			}
		};
	const handlePlaceSelection = (place, mapInstance) => {
		if (readOnly) return;
		if (place && place.geometry) {
			mapInstance.setCenter(place.geometry.location);
			mapInstance.setZoom(15);

			if (markerRef.current) {
				markerRef.current.setMap(null);
			}

			const marker = new google.maps.Marker({
				position: place.geometry.location,
				map: mapInstance,
				title: place.name,
			});

			markerRef.current = marker;
			setLocation(place.formatted_address || place.name);
		}
	};

	const initializePolygon = (coordinates, mapInstance) => {
		setFactoryCoordinates(coordinates);
			const polygon = createPolygon(coordinates, mapInstance);
			const bounds = new google.maps.LatLngBounds();
			polygon.getPath().forEach((latLng) => bounds.extend(latLng));
			mapInstance.fitBounds(bounds);
		}

	const clearGeofence = () => {
		if (readOnly) return;
		stopDrawing();
		// Remove the polygon from the map but keep the location marker
		if (currentPolygonRef.current) {
			currentPolygonRef.current.setMap(null);
			currentPolygonRef.current = null;  // Reset the polygon reference
		}
		clearPolygonListeners();

		setFactoryCoordinates([]);
		onCoordinatesChange?.([]);
	};

    const validateCoOrdinates = () => {
    const errors = {};
    let success = true;
    const lat = parseFloat(latLngInput.lat);
    const lng = parseFloat(latLngInput.lng);

    if (isNaN(lat)) {
        errors.latitude = "Enter valid latitude values";
        success = false;
    } else if (lat < -90 || lat > 90) {
        errors.latitude = "Latitude must be between -90 and 90";
        success = false;
    }

    if (isNaN(lng)) {
        errors.longitude = "Enter valid longitude values";
        success = false;
    } else if (lng < -180 || lng > 180) {
        errors.longitude = "Longitude must be between -180 and 180";
        success = false;
    }
    return { errors, success };
};

const handleSearchByCoordinates = () => {
    if (!map) return;
    
    const validation = validateCoOrdinates();
    if (!validation.success) {
        setErrors(validation.errors);
        return;
    }

    const lat = parseFloat(latLngInput.lat);
    const lng = parseFloat(latLngInput.lng);
    
    const location = new window.google.maps.LatLng(lat, lng);
    map.setCenter(location);
    map.setZoom(15);

    if (markerRef.current) {
        markerRef.current.setMap(null);
    }

    const marker = new window.google.maps.Marker({
        position: location,
        map: map,
    });

    markerRef.current = marker;
    setErrors({});
};

	useEffect(() => {
		if (window.google && window.google.maps && window.google.maps.places) {
			initMap();
		} else {
			const checkGoogleMapsLoaded = setInterval(() => {
				if (window.google && window.google.maps && window.google.maps.places) {
					clearInterval(checkGoogleMapsLoaded);
					initMap();
				}
			}, 100);
			return () => clearInterval(checkGoogleMapsLoaded);
		}

		return () => {
			removeMapClickListener();
			clearPolygonListeners();
		};
	}, []);

	return (
		<div className="geofence-container">
			{noExtraField ? null :
			<div className="location-search">
				<div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.5rem",flexWrap: "wrap"}}>
					<div style={{ flex: 0.5, minWidth: "250px" }}>
						<Input
							id="location-input"
							label="Search by place name"
							type="text"
							value={location}
							onChange={(e) => setLocation(e.target.value)}
							sx={{ width: "100%" }}
						/>
					</div>
					<div style={{ color: "#666", padding: "0 0.5rem", marginBottom: "0.5rem"}}> OR </div>				
					<div style={{ display: "flex", gap: "0.5rem", minWidth: "200px"}}>
						<Input
							label="Latitude"
                            name="latitude"
							type="text"
							value={latLngInput.lat}
							onChange={(e) => setLatLngInput({ ...latLngInput, lat: e.target.value })}
							placeholder="e.g. 28.524782"
                            error={errors.latitude}
						/>
						<Input
							label="Longitude"
                            name="longitude"
							type="text"
							value={latLngInput.lng}
							onChange={(e) => setLatLngInput({ ...latLngInput, lng: e.target.value })}
							placeholder="e.g. 77.277073"
                            error={errors.longitude}
						/>
						<Button text="Search" style={{ ...buttonClasses.lynkitOrangeFill, maxWidth:'120px', maxHeight:"35px"} } onClick={handleSearchByCoordinates} />
					</div>
				</div>
			</div>
			}
			<div style={{ position: "relative" }}>
				<div ref={mapRef} className="map" style={{ height: "500px", width: "100%" }}></div>
				<div className="controls">
					{readOnly ? null : (
						<button type="button" onClick={isDrawing ? stopDrawing : startDrawing} className="clear-button">
							{isDrawing ? "Finish Geofence" : "Draw Geofence"}
						</button>
					)}
					{readOnly? null : <button type="button" onClick={clearGeofence} className="clear-button">
						Clear Geofence
					</button>}
				</div>
			</div>

			{factoryCoordinates.length > 0 && (
				<div className="coordinate_container">
					<h6>Factories Co-ordinates</h6>
					<div className="coordinate_box">
						{factoryCoordinates.map((coordinate, index) => (
							<div key={index}>
								<ul>
									<li style={{color: "var(--text-color)"}}>
										<div>Latitude: {coordinate.lat}</div>
										<div>Longitude: {coordinate.lng}</div>
									</li>
								</ul>
							</div>
						))}
					</div>
				</div>
			)}
		</div>
	);
};

export default Geofence;
